import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useGlobalLocation } from '@/contexts/LocationContext';
import { formatDateNL, formatEUR } from '@/lib/format';
import { contractStatus } from '@/lib/contract';
import { VerlengDialog } from '@/components/VerlengDialog';
import { BulkVerlengDialog } from '@/components/BulkVerlengDialog';
import { Users as UsersIcon, Search, Plus, Upload, Download, FileSpreadsheet, Filter, ChevronRight, Building2, History } from 'lucide-react';

const ROLE_BADGE = {
  medewerker: 'bg-slate-100 text-slate-700',
  junior: 'bg-blue-100 text-blue-700',
  senior: 'bg-indigo-100 text-indigo-700',
  teamleider: 'bg-purple-100 text-purple-700',
  bedrijfsleider: 'bg-amber-100 text-amber-800',
};

const CONTRACT_BADGE = {
  vast: 'bg-green-100 text-green-800',
  tijdelijk: 'bg-yellow-100 text-yellow-800',
  oproep: 'bg-orange-100 text-orange-800',
};

export default function Medewerkers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { globalLocation, setGlobalLocation } = useGlobalLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: 'actief', search: '' });
  const [showImport, setShowImport] = useState(false);
  const [showHistorie, setShowHistorie] = useState(false);
  const [verlengEmp, setVerlengEmp] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulk, setShowBulk] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [downloading, setDownloading] = useState('');

  const isOwnerOrAdmin = user?.role === 'owner' || user?.role === 'admin';

  async function downloadXlsx(url, filename, key) {
    setDownloading(key);
    try {
      const resp = await api.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = blobUrl; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.error('[medewerkers] download failed', e);
      alert('Download mislukt — probeer het opnieuw.');
    } finally {
      setDownloading('');
    }
  }
  const handleExport = () => downloadXlsx('/employees/export', 'medewerkers_export.xlsx', 'export');
  const handleTemplate = () => downloadXlsx('/employees/import/template', 'medewerkers_importsjabloon.xlsx', 'template');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (globalLocation !== 'all') params.set('location_slug', globalLocation);
      Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
      const [empResp, locResp] = await Promise.all([
        api.get(`/employees?${params}`),
        api.get('/locations'),
      ]);
      setEmployees(empResp.data);
      setLocations(locResp.data);
    } catch (e) {
      console.error('[medewerkers] load failed', e);
    } finally {
      setLoading(false);
    }
  }, [filters, globalLocation]);

  useEffect(() => {
    load();
  }, [load]);

  const locationById = useMemo(() => {
    const m = {};
    locations.forEach((l) => (m[l.id] = l));
    return m;
  }, [locations]);

  const counts = useMemo(() => ({
    total: employees.length,
    actief: employees.filter((e) => e.status === 'actief').length,
    uitdienst: employees.filter((e) => e.status === 'uit dienst').length,
  }), [employees]);

  const contractFilter = searchParams.get('contract') || '';
  const shown = useMemo(() => {
    if (!contractFilter) return employees;
    return employees.filter((e) => {
      const st = contractStatus(e.contract_einddatum);
      if (contractFilter === 'beide') return st === 'verlopen' || st === 'bijna';
      return st === contractFilter;
    });
  }, [employees, contractFilter]);

  const setContractFilter = (v) => {
    const next = new URLSearchParams(searchParams);
    if (v) next.set('contract', v); else next.delete('contract');
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-4" data-testid="page-medewerkers">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <UsersIcon size={18} /> Medewerkers
          </h2>
          <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600" data-testid="emp-count">
            {loading ? '…' : `${counts.total} (${counts.actief} actief)`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {isOwnerOrAdmin && (
            <>
              <button
                onClick={handleExport}
                disabled={downloading === 'export'}
                data-testid="btn-export-excel"
                className="flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-60"
              >
                <Download size={14} /> {downloading === 'export' ? 'Bezig…' : 'Exporteer (Excel)'}
              </button>
              <button
                onClick={handleTemplate}
                disabled={downloading === 'template'}
                data-testid="btn-download-template"
                className="flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-60"
              >
                <FileSpreadsheet size={14} /> Sjabloon
              </button>
              <button
                onClick={() => setShowImport(true)}
                data-testid="btn-import-excel"
                className="flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"
              >
                <Upload size={14} /> Importeer uit Excel
              </button>
              <button
                onClick={() => setShowHistorie(true)}
                data-testid="btn-import-historie"
                className="flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"
              >
                <History size={14} /> Import-historie
              </button>
            </>
          )}
          <button
            onClick={() => setShowCreate(true)}
            data-testid="btn-create-employee"
            className="flex items-center gap-1.5 rounded-md bg-brand-navy px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light"
          >
            <Plus size={14} /> Nieuwe medewerker
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white p-3">
        <div className="flex items-center gap-1 rounded-md border border-slate-300 px-2">
          <Search size={14} className="text-slate-400" />
          <input
            type="search"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            data-testid="emp-search"
            placeholder="Zoek naam of nummer"
            className="border-0 px-1 py-1 text-sm focus:outline-none"
          />
        </div>
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          data-testid="filter-status"
          className="rounded-md border border-slate-300 px-3 py-1.5 text-sm"
        >
          <option value="">Alle statussen</option>
          <option value="actief">Actief</option>
          <option value="uit dienst">Uit dienst</option>
        </select>
        <select
          value={globalLocation}
          onChange={(e) => setGlobalLocation(e.target.value)}
          data-testid="filter-location"
          className="rounded-md border border-slate-300 px-3 py-1.5 text-sm"
        >
          <option value="all">Alle locaties</option>
          {locations.map((l) => (
            <option key={l.slug} value={l.slug}>{l.name}</option>
          ))}
        </select>
        <select
          value={contractFilter}
          onChange={(e) => setContractFilter(e.target.value)}
          data-testid="filter-contract"
          className="rounded-md border border-slate-300 px-3 py-1.5 text-sm"
        >
          <option value="">Alle contracten</option>
          <option value="beide">Contract verlopen/bijna verlopen</option>
          <option value="verlopen">Contract verlopen</option>
          <option value="bijna">Loopt af ≤30 dagen</option>
        </select>
      </div>

      {contractFilter && isOwnerOrAdmin && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm" data-testid="bulk-bar">
          <label className="flex items-center gap-2 text-slate-700">
            <input
              type="checkbox"
              checked={shown.length > 0 && selectedIds.length === shown.length}
              onChange={(e) => setSelectedIds(e.target.checked ? shown.map((x) => x.id) : [])}
              data-testid="bulk-select-all"
            />
            Selecteer alles (gefilterd)
          </label>
          <span className="text-slate-500" data-testid="bulk-selected-count">{selectedIds.length} geselecteerd</span>
          <button
            type="button"
            onClick={() => setShowBulk(true)}
            disabled={selectedIds.length === 0}
            data-testid="btn-bulk-verlengen"
            className="rounded-md bg-brand-navy px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            Verleng geselecteerden…
          </button>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        {loading ? (
          <p className="p-6 text-sm text-slate-500">Laden…</p>
        ) : shown.length === 0 ? (
          <p className="p-6 text-sm text-slate-500" data-testid="emp-empty">Geen medewerkers gevonden.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  {contractFilter && isOwnerOrAdmin && <th className="px-3 py-2.5"></th>}
                  <th className="px-4 py-2.5 text-left">Pnr</th>
                  <th className="px-4 py-2.5 text-left">Naam</th>
                  <th className="px-4 py-2.5 text-left">Rol</th>
                  <th className="px-4 py-2.5 text-left">Locatie</th>
                  <th className="px-4 py-2.5 text-left">Contract</th>
                  <th className="px-4 py-2.5 text-right">Uren</th>
                  <th className="px-4 py-2.5 text-right">Uurloon</th>
                  <th className="px-4 py-2.5 text-left">In/Uit dienst</th>
                  <th className="px-4 py-2.5 text-left">Contract t/m</th>
                  <th className="px-4 py-2.5 text-right">Status</th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {shown.map((e) => {
                  const loc = e.home_location_id ? locationById[e.home_location_id] : null;
                  return (
                    <tr
                      key={e.id}
                      data-testid={`emp-row-${e.personeelsnummer}`}
                      onClick={() => navigate(`/medewerkers/${e.id}`)}
                      className="cursor-pointer border-t border-slate-100 hover:bg-slate-50"
                    >
                      {contractFilter && isOwnerOrAdmin && (
                        <td className="px-3 py-2" onClick={(ev) => ev.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(e.id)}
                            onChange={(ev) => setSelectedIds((prev) => (
                              ev.target.checked ? [...prev, e.id] : prev.filter((x) => x !== e.id)))}
                            data-testid={`bulk-check-${e.personeelsnummer}`}
                          />
                        </td>
                      )}
                      <td className="px-4 py-2 text-slate-600 tabular-nums">{e.personeelsnummer}</td>
                      <td className="px-4 py-2 font-medium text-slate-900">{e.name}</td>
                      <td className="px-4 py-2">
                        <span className={`rounded px-2 py-0.5 text-xs ${ROLE_BADGE[e.role] || 'bg-slate-100'}`}>{e.role}</span>
                      </td>
                      <td className="px-4 py-2 text-slate-700">{loc?.name || e.bedrijfsnaam || '—'}</td>
                      <td className="px-4 py-2">
                        <span className={`rounded px-2 py-0.5 text-xs ${CONTRACT_BADGE[e.contract_type] || 'bg-slate-100'}`}>{e.contract_type}</span>
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">{e.contract_hours || 0}u</td>
                      <td className="px-4 py-2 text-right tabular-nums">{e.uurloon ? formatEUR(e.uurloon) : '—'}</td>
                      <td className="px-4 py-2 text-xs text-slate-600">
                        {formatDateNL(e.datum_in_dienst)}
                        {e.datum_uit_dienst && <> → {formatDateNL(e.datum_uit_dienst)}</>}
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {e.contract_einddatum ? (
                          <span
                            data-testid={`emp-contract-${e.personeelsnummer}`}
                            className={`rounded px-1.5 py-0.5 ${
                              contractStatus(e.contract_einddatum) === 'verlopen' ? 'bg-red-100 text-red-800'
                                : contractStatus(e.contract_einddatum) === 'bijna' ? 'bg-amber-100 text-amber-800'
                                  : 'text-slate-600'}`}
                          >
                            {formatDateNL(e.contract_einddatum)}
                          </span>
                        ) : <span className="text-slate-400">—</span>}
                        {contractFilter && isOwnerOrAdmin && (
                          <button
                            type="button"
                            onClick={(ev) => { ev.stopPropagation(); setVerlengEmp(e); }}
                            data-testid={`emp-verleng-${e.personeelsnummer}`}
                            className="ml-2 rounded border border-slate-300 px-1.5 py-0.5 text-[11px] hover:bg-slate-50"
                          >
                            Verlengen…
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <span className={`rounded px-2 py-0.5 text-xs ${e.status === 'actief' ? 'bg-green-100 text-green-800' : 'bg-slate-200 text-slate-600'}`}>
                          {e.status}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <ChevronRight size={16} className="text-slate-400" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showImport && <ImportDialog onClose={() => { setShowImport(false); load(); }} />}
      {showBulk && (
        <BulkVerlengDialog
          employees={shown.filter((e) => selectedIds.includes(e.id))}
          onClose={() => { setShowBulk(false); setSelectedIds([]); load(); }}
          onDone={() => load()}
        />
      )}
      {showHistorie && <ImportHistorieDialog onClose={() => setShowHistorie(false)} />}
      {verlengEmp && (
        <VerlengDialog
          employee={verlengEmp}
          onClose={() => setVerlengEmp(null)}
          onDone={() => load()}
        />
      )}
      {showCreate && <CreateDialog locations={locations} onClose={(reload) => { setShowCreate(false); if (reload) load(); }} />}
    </div>
  );
}

function ImportHistorieDialog({ onClose }) {
  const [logs, setLogs] = useState(null);

  useEffect(() => {
    api.get('/employees/import-logs/recent', { params: { limit: 50 } })
      .then((r) => setLogs(r.data.logs || []))
      .catch(() => setLogs([]));
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="import-historie-dialog">
      <div className="max-h-[85vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white p-5 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900">Import-historie</h3>
          <button type="button" onClick={onClose} data-testid="historie-close" className="text-sm text-slate-500 hover:text-slate-800">
            Sluiten
          </button>
        </div>
        <p className="mb-3 text-xs text-slate-500">
          Alleen-lezen overzicht van de laatste importruns (nieuwste eerst). Een import kan niet worden teruggedraaid.
        </p>
        {logs === null ? (
          <p className="text-sm text-slate-500">Laden…</p>
        ) : logs.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="historie-empty">Nog geen importruns.</p>
        ) : (
          <div className="space-y-2">
            {logs.map((l) => (
              <div key={l.id || l.import_run_id} className="rounded-lg border border-slate-200 p-3" data-testid={`historie-run-${l.import_run_id}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="text-sm font-medium text-slate-900">
                    {l.filename || '(geen bestandsnaam)'}
                  </p>
                  <p className="text-[11px] text-slate-500">
                    {l.timestamp ? new Date(l.timestamp).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                    {l.actor_email ? ` · ${l.actor_email}` : ''}
                  </p>
                </div>
                <p className="mt-1 text-xs text-slate-600">
                  <span className="text-green-700">{l.inserted ?? 0} nieuw</span> ·{' '}
                  <span className="text-amber-700">{l.updated ?? 0} gewijzigd</span> ·{' '}
                  {l.unchanged ?? 0} ongewijzigd ·{' '}
                  <span className={l.failed ? 'text-red-700' : ''}>{l.failed ?? 0} fouten</span>
                </p>
                {(l.signalen || []).length > 0 && (
                  <div className="mt-1 space-y-0.5">
                    {l.signalen.map((sg) => (
                      <p key={sg.key} className="text-[11px] text-slate-500">
                        <b>{sg.count}×</b> {sg.label}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function ImportDialog({ onClose }) {
  const [file, setFile] = useState(null);
  const [phase, setPhase] = useState('select'); // select | analyzing | report | committing | done
  const [report, setReport] = useState(null);
  const [commitResult, setCommitResult] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('gewijzigd');

  async function runDryRun() {
    if (!file) return;
    setPhase('analyzing'); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/employees/import/dry-run', fd, { timeout: 60000 });
      setReport(data);
      const s = data.summary;
      setTab(s.gewijzigd ? 'gewijzigd' : s.nieuw ? 'nieuw' : s.fouten ? 'fouten' : 'ongewijzigd');
      setPhase('report');
    } catch (e) {
      setError(e.response?.data?.detail || 'Analyse mislukt');
      setPhase('select');
    }
  }

  async function runCommit() {
    if (!file) return;
    setPhase('committing'); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/employees/import/commit', fd, { timeout: 90000 });
      setCommitResult(data);
      setPhase('done');
    } catch (e) {
      setError(e.response?.data?.detail || 'Doorvoeren mislukt');
      setPhase('report');
    }
  }

  const s = report?.summary || {};
  const TABS = [
    ['nieuw', `Nieuw (${s.nieuw || 0})`],
    ['gewijzigd', `Gewijzigd (${s.gewijzigd || 0})`],
    ['ongewijzigd', `Ongewijzigd (${s.ongewijzigd || 0})`],
    ['fouten', `Fouten (${s.fouten || 0})`],
    ['niet_in_bestand', `Niet in bestand (${s.niet_in_bestand || 0})`],
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-3xl rounded-xl bg-white p-6 shadow-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="import-dialog">
        <h3 className="text-lg font-semibold text-brand-navy mb-1">Medewerkers importeren uit Excel</h3>
        <p className="text-sm text-slate-600 mb-4">
          De rauwe TrainStation-export mag direct geüpload worden. Lege cellen laten bestaande gegevens ongemoeid.
          BSN, PIN en rol kunnen nooit via de import gezet worden; niemand wordt via een bestand uit dienst gezet.
        </p>

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 mb-3" data-testid="import-error">{error}</div>
        )}

        {(phase === 'select' || phase === 'analyzing') && (
          <div className="space-y-4">
            <label className="block rounded-lg border-2 border-dashed border-slate-300 p-6 text-center cursor-pointer hover:bg-slate-50">
              <input type="file" accept=".xlsx" className="hidden" data-testid="import-file-input"
                onChange={(e) => { setFile(e.target.files?.[0] || null); setError(''); }} />
              <Upload size={20} className="mx-auto text-slate-400 mb-2" />
              <span className="text-sm text-slate-700">{file ? file.name : 'Kies een .xlsx-bestand'}</span>
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50">Annuleren</button>
              <button onClick={runDryRun} disabled={!file || phase === 'analyzing'} data-testid="btn-analyze-import"
                className="rounded-md bg-brand-navy px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light disabled:opacity-60">
                {phase === 'analyzing' ? 'Analyseren…' : 'Analyseren'}
              </button>
            </div>
          </div>
        )}

        {(phase === 'report' || phase === 'committing') && report && (
          <div className="flex flex-col overflow-hidden">
            {(report.headers?.refused?.length > 0 || report.headers?.unknown?.length > 0
              || report.headers?.ignored?.length > 0 || report.signalen?.length > 0) && (
              <div className="mb-2 space-y-1 text-xs">
                {report.headers.refused.length > 0 && (
                  <div className="rounded bg-red-50 px-2 py-1 text-red-700" data-testid="import-refused">
                    Geweigerde kolommen (BSN/PIN/rol mogen niet via import): {report.headers.refused.join(', ')}
                  </div>
                )}
                {report.headers.ignored?.length > 0 && (
                  <div className="rounded bg-slate-100 px-2 py-1 text-slate-600" data-testid="import-ignored">
                    Kolommen niet gebruikt: {report.headers.ignored.join(', ')}
                  </div>
                )}
                {report.headers.unknown.length > 0 && (
                  <div className="rounded bg-amber-50 px-2 py-1 text-amber-700">
                    Onbekende kolommen genegeerd: {report.headers.unknown.join(', ')}
                  </div>
                )}
                {report.signalen?.length > 0 && (
                  <div className="rounded bg-slate-50 border border-slate-200 px-2 py-1 text-slate-700" data-testid="import-signalen">
                    {report.signalen.map((sg) => (
                      <div key={sg.key} data-testid={`import-signaal-${sg.key}`}>
                        <b>{sg.count}×</b> {sg.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="flex flex-wrap gap-1 border-b border-slate-200 mb-3">
              {TABS.map(([k, label]) => (
                <button key={k} onClick={() => setTab(k)} data-testid={`import-tab-${k}`}
                  className={`px-3 py-1.5 text-sm border-b-2 -mb-px ${tab === k ? 'border-brand-gold text-brand-navy font-medium' : 'border-transparent text-slate-500'}`}>
                  {label}
                </button>
              ))}
            </div>

            <div className="overflow-y-auto flex-1 min-h-[200px]" data-testid="import-report-body">
              {tab === 'nieuw' && (report.nieuw.length ? report.nieuw.map((r) => (
                <div key={r.personeelsnummer} className="rounded border border-green-200 bg-green-50 p-2 mb-2 text-sm">
                  <b>{r.name}</b> <span className="text-slate-500">#{r.personeelsnummer}</span>
                  <div className="text-xs text-slate-600 mt-1">{Object.entries(r.fields).map(([k, v]) => `${k}: ${v}`).join(' · ')}</div>
                  {r.notes?.map((n, i) => <p key={`n${i}`} className="text-xs text-slate-500 mt-1">ℹ {n}</p>)}
                  {r.warnings.map((w, i) => <p key={i} className="text-xs text-amber-700 mt-1">⚠ {w}</p>)}
                </div>
              )) : <Empty />)}

              {tab === 'gewijzigd' && (report.gewijzigd.length ? report.gewijzigd.map((r) => (
                <div key={r.personeelsnummer} className="rounded border border-blue-200 bg-blue-50 p-2 mb-2 text-sm">
                  <b>{r.name}</b> <span className="text-slate-500">#{r.personeelsnummer}</span>
                  <table className="w-full text-xs mt-1">
                    <tbody>
                      {r.diffs.map((d) => (
                        <tr key={d.key}>
                          <td className="pr-2 text-slate-500">{d.field}</td>
                          <td className="pr-2 text-red-600 line-through">{d.old === null || d.old === undefined ? '—' : String(d.old)}</td>
                          <td className="text-green-700 font-medium">{d.new === null || d.new === undefined ? 'leeg' : String(d.new)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {r.notes?.map((n, i) => <p key={`n${i}`} className="text-xs text-slate-500 mt-1">ℹ {n}</p>)}
                  {r.warnings.map((w, i) => <p key={i} className="text-xs text-amber-700 mt-1 font-medium">⚠ {w}</p>)}
                </div>
              )) : <Empty />)}

              {tab === 'ongewijzigd' && (
                <div className="text-sm text-slate-600">
                  <p>{report.ongewijzigd.length} medewerker(s) ongewijzigd.</p>
                  {report.ongewijzigd.filter((r) => r.notes?.length).map((r) => (
                    <div key={r.personeelsnummer} className="mt-2 text-xs">
                      <b>{r.name}</b> <span className="text-slate-400">#{r.personeelsnummer}</span>
                      {r.notes.map((n, i) => <p key={i} className="text-slate-500">ℹ {n}</p>)}
                    </div>
                  ))}
                </div>
              )}

              {tab === 'fouten' && (report.fouten.length ? report.fouten.map((f, i) => (
                <div key={i} className="rounded border border-red-200 bg-red-50 p-2 mb-2 text-sm text-red-700">
                  Rij {f.row}: {ERR_NL[f.reason] || f.reason}{f.bedrijfsnaam ? ` — "${f.bedrijfsnaam}"` : ''}{f.value ? ` (${f.field}: ${f.value})` : ''}
                </div>
              )) : <Empty />)}

              {tab === 'niet_in_bestand' && (report.niet_in_bestand.length ? (
                <>
                  <p className="text-xs text-slate-500 mb-2">Alleen informatief — deze worden NOOIT automatisch uit dienst gezet.</p>
                  {report.niet_in_bestand.map((r) => (
                    <div key={r.personeelsnummer} className="text-sm py-0.5">{r.name} <span className="text-slate-400">#{r.personeelsnummer} · {r.locatie || '—'}</span></div>
                  ))}
                </>
              ) : <Empty />)}
            </div>

            <div className="flex justify-between items-center gap-2 pt-3 border-t border-slate-200 mt-3">
              <button onClick={onClose} className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50">Annuleren</button>
              <button onClick={runCommit} disabled={phase === 'committing' || (!s.nieuw && !s.gewijzigd)} data-testid="btn-commit-import"
                className="rounded-md bg-brand-navy px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light disabled:opacity-60">
                {phase === 'committing' ? 'Doorvoeren…' : `Wijzigingen doorvoeren (${s.nieuw || 0} nieuw, ${s.gewijzigd || 0} bijgewerkt)`}
              </button>
            </div>
          </div>
        )}

        {phase === 'done' && commitResult && (
          <div className="space-y-3" data-testid="import-done">
            <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
              Import voltooid: <b>{commitResult.inserted}</b> nieuw, <b>{commitResult.updated}</b> bijgewerkt,
              {' '}{commitResult.unchanged} ongewijzigd, {commitResult.failed} fout(en).
            </div>
            <div className="flex justify-end">
              <button onClick={onClose} data-testid="btn-close-import"
                className="rounded-md bg-brand-navy px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light">Sluiten</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Empty() {
  return <div className="text-sm text-slate-400 py-6 text-center">Geen items in deze categorie.</div>;
}

const ERR_NL = {
  missing_personeelsnummer: 'Personeelsnummer ontbreekt',
  unknown_location: 'Onbekende bedrijfsnaam/locatie',
  invalid_date: 'Ongeldige datum',
  invalid_number: 'Ongeldig getal',
  invalid_contract_type: 'Ongeldig contract type',
  parse_error: 'Kon rij niet lezen',
};

function CreateDialog({ locations, onClose }) {
  const [form, setForm] = useState({
    personeelsnummer: '', name: '', role: 'medewerker',
    home_location_id: locations[0]?.id || '',
    contract_type: 'vast', contract_hours: 0,
    datum_in_dienst: new Date().toISOString().slice(0, 10),
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await api.post('/employees', { ...form, contract_hours: parseFloat(form.contract_hours) || 0 });
      onClose(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Aanmaken mislukt');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <form onSubmit={handleSubmit} className="w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl space-y-3" data-testid="create-emp-form">
        <h3 className="text-lg font-semibold text-slate-900">Nieuwe medewerker</h3>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Personeelsnummer" required value={form.personeelsnummer} onChange={(v) => setForm({ ...form, personeelsnummer: v })} testid="emp-pnr" />
          <Field label="Naam" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="emp-name" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <SelectField label="Rol" value={form.role} onChange={(v) => setForm({ ...form, role: v })}
            options={[['medewerker','Medewerker'],['junior','Junior'],['senior','Senior'],['teamleider','Teamleider'],['bedrijfsleider','Bedrijfsleider']]} testid="emp-role" />
          <SelectField label="Thuislocatie" value={form.home_location_id} onChange={(v) => setForm({ ...form, home_location_id: v })}
            options={locations.map((l) => [l.id, l.name])} testid="emp-location" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <SelectField label="Contracttype" value={form.contract_type} onChange={(v) => setForm({ ...form, contract_type: v })}
            options={[['vast','Vast'],['tijdelijk','Tijdelijk'],['oproep','Oproep']]} testid="emp-contract" />
          <Field label="Uren/week" type="number" value={form.contract_hours} onChange={(v) => setForm({ ...form, contract_hours: v })} testid="emp-hours" />
        </div>
        <Field label="Datum in dienst" type="date" value={form.datum_in_dienst} onChange={(v) => setForm({ ...form, datum_in_dienst: v })} testid="emp-startdate" />

        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={() => onClose(false)} className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50">
            Annuleren
          </button>
          <button type="submit" disabled={submitting} data-testid="btn-submit-emp"
            className="rounded-md bg-brand-navy px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-light disabled:opacity-60">
            {submitting ? 'Bezig…' : 'Aanmaken'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', required, testid }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <input type={type} required={required} value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm" />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
